import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { ColumnModel } from '@syncfusion/ej2-react-grids'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import Button from '@components/common/Button/Button.component'
import { ClipMode } from '@constants/Common/GridComponent.contants'
import {
  IPhaseItems,
  IPhases,
  TxPlanProcedureModalContext,
} from '@dataTypes/treatmentPlan.types'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
  stringifyTxPlanItemDragPayload,
  TX_PLAN_ITEM_DRAG_MIME,
  txPlanProcedureProviderHighlightRowKey,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import { filterTxPlanInsuranceEstimateColumns } from '@utils/tx-plan-procedure-grid-columns.util'

const getTxPlanFeeEditKey = (
  tabKey: string,
  phaseOriginalIdx: number,
  itemId: string,
): string => `${tabKey}:${phaseOriginalIdx}:${itemId}`

interface TxPlanFeeEditCellProps {
  charges: number
  isEditMode: boolean
  allowManualFeeOverride: boolean
  isActiveEditor: boolean
  onStartEdit: () => void
  onSave: (clinicFee: number) => void
  onCancelEdit: () => void
}

const TxPlanFeeEditCell = memo(
  ({
    charges,
    isEditMode,
    allowManualFeeOverride,
    isActiveEditor,
    onStartEdit,
    onSave,
    onCancelEdit,
  }: TxPlanFeeEditCellProps): React.ReactElement => {
    const [draftValue, setDraftValue] = useState(() => String(charges))
    const inputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
      if (isActiveEditor) {
        setDraftValue(String(charges))
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }, [charges, isActiveEditor])

    if (!isEditMode || !allowManualFeeOverride) {
      return <>{formatNumber(charges, true)}</>
    }

    if (isActiveEditor) {
      const commitFee = (): void => {
        const parsedFee = Number.parseFloat(draftValue)
        onCancelEdit()
        if (Number.isNaN(parsedFee) || parsedFee < 0) {
          return
        }
        if (parsedFee !== charges) {
          onSave(parsedFee)
        }
      }

      return (
        <div className="tx-plan-fee-edit d-flex align-items-center gap-1">
          <Form.Control
            ref={inputRef}
            type="text"
            className="tx-plan-fee-edit-input"
            size="sm"
            aria-label="Clinic fee"
            value={draftValue}
            onClick={(e) => e.stopPropagation()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDraftValue(e.target.value)
            }}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                commitFee()
              } else if (e.key === 'Escape') {
                onCancelEdit()
              }
            }}
            onBlur={commitFee}
          />
        </div>
      )
    }

    return (
      <div className="tx-plan-fee-edit d-flex align-items-center gap-1">
        <span className="tx-plan-fee-edit-label">
          {formatNumber(charges, true)}
        </span>
        <button
          type="button"
          className="unstyled-button mgc_pencil_fill tx-plan-fee-edit-icon"
          aria-label="Edit clinic fee"
          title="Edit clinic fee"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onStartEdit()
          }}
        />
      </div>
    )
  },
)

TxPlanFeeEditCell.displayName = 'TxPlanFeeEditCell'

function providerSelectOptionsForRow(
  baseOptions: Provider[],
  lookupProviders: Provider[],
  selectedProviderId?: string | null,
): Provider[] {
  const id =
    typeof selectedProviderId === 'string' &&
    selectedProviderId.trim().length > 0
      ? selectedProviderId
      : null
  if (!id) return baseOptions
  if (baseOptions.some((p) => p.provider_id === id)) return baseOptions
  const fallback = lookupProviders.find((p) => p.provider_id === id)
  return fallback !== undefined ? [...baseOptions, fallback] : baseOptions
}

export type TxPlanPhaseFormState = {
  planName: string
  insuranceProvider: string | null
  selectedInsurance: InsurancePlan | null
  selectedDiscountId: string
  customDiscount: number
  phases: IPhases[]
  activeAccordionKeys: string[]
  procedureModal: TxPlanProcedureModalContext | null
}

export type TxPlanProcedureProviderChangeArgs = {
  tabKey: string
  phaseOriginalIdx: number
  item: IPhaseItems
  providerId: string
}

export type TxPlanProcedureFeeChangeArgs = {
  tabKey: string
  phaseOriginalIdx: number
  item: IPhaseItems
  clinicFee: number
}

export type TxPlanPhaseProcedureGridInteractionRef = {
  saveHistorySnapshot: (tabKey: string) => void
  editToothSelection: (
    tabKey: string,
    phaseOriginalIdx: number,
    item: IPhaseItems,
  ) => void
  clearTreatmentPlanDragUi: () => void
}

export interface TxPlanPhaseProcedureGridProps {
  tabKey: string
  originalIdx: number
  visibleItems: IPhaseItems[]
  isEditMode: boolean
  draggingItemId: string | null
  providerList: Provider[]
  /** Full staff list for resolving saved provider_id in read-only mode */
  providerLookupList?: Provider[]
  /** Row keys (from txPlanProcedureProviderHighlightRowKey) flagged after save validation */
  missingProviderRowKeys?: ReadonlySet<string>
  allowManualFeeOverride?: boolean
  showInsuranceEstimate?: boolean
  onProcedureProviderChange: (args: TxPlanProcedureProviderChangeArgs) => void
  onProcedureFeeChange?: (args: TxPlanProcedureFeeChangeArgs) => void
  setFormStates: React.Dispatch<
    React.SetStateAction<Record<string, TxPlanPhaseFormState>>
  >
  setDraggingItemId: React.Dispatch<React.SetStateAction<string | null>>
  interactionRef: React.MutableRefObject<TxPlanPhaseProcedureGridInteractionRef>
  enableVirtualization?: boolean
}

const TxPlanPhaseProcedureGridInner = ({
  tabKey,
  originalIdx,
  visibleItems,
  isEditMode,
  draggingItemId,
  providerList,
  providerLookupList = providerList,
  missingProviderRowKeys,
  allowManualFeeOverride = false,
  showInsuranceEstimate = true,
  onProcedureProviderChange,
  onProcedureFeeChange,
  setFormStates,
  setDraggingItemId,
  interactionRef,
  enableVirtualization = false,
}: TxPlanPhaseProcedureGridProps): React.ReactElement => {
  const [editingFeeKey, setEditingFeeKey] = useState<string | null>(null)

  const rowModels = useMemo(() => {
    return visibleItems.map((p) => ({
      ...p,
      code: p.procedure_settings?.procedure?.procedure_code,
      description: p.procedure_settings?.procedure?.description,
      allowed_amt: p.allowed_amount ?? 0,
      write_off: p.write_off ?? 0,
    }))
  }, [visibleItems])

  const columns = useMemo((): ColumnModel[] => {
    const toothSurfaceTemplate = (
      props: { surface?: string | null } & IPhaseItems,
    ): JSX.Element => {
      const toothSurfaceEntries = getTSFromToothSurfaces(props.tooth_surfaces)
      const surfaceEntries = parseSurfaceString(props.surface)
      const parsedEntries =
        toothSurfaceEntries.length > 0 ? toothSurfaceEntries : surfaceEntries
      const hasNoAreaType =
        parsedEntries.length === 0 ||
        parsedEntries.every((e) => e.area_type === null)

      const showEditPencil =
        isEditMode && !hasNoAreaType && parsedEntries.length > 0

      return (
        <div className="tx-plan-fee-edit template-cell-tooth-surface-wrapper">
          <span
            className={`template-cell-tooth-surface tx-plan-fee-edit-label ${
              hasNoAreaType && isEditMode
                ? 'template-cell-tooth-surface-empty'
                : ''
            }`}
          >
            {hasNoAreaType ? (
              isEditMode ? (
                <Button
                  size="xs"
                  className="px-1 text-primary add-ts-button gap-1"
                  onClick={() =>
                    interactionRef.current.editToothSelection(
                      tabKey,
                      originalIdx,
                      props,
                    )
                  }
                >
                  <i className="mgc mgc_add_line" aria-hidden /> Add T/S
                </Button>
              ) : null
            ) : (
              formatToothSurfaceEntriesForDisplay(parsedEntries)
            )}
          </span>
          {showEditPencil && (
            <button
              type="button"
              aria-label="Edit tooth/surface"
              title="Edit tooth/surface"
              className="unstyled-button mgc_pencil_fill tx-plan-fee-edit-icon"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                interactionRef.current.editToothSelection(
                  tabKey,
                  originalIdx,
                  props,
                )
              }}
            />
          )}
        </div>
      )
    }

    const builtColumns: ColumnModel[] = [
      ...(isEditMode
        ? [
            {
              field: '__tx_drag',
              headerText: '',
              width: '30px',
              allowSorting: false,
              template: (props: IPhaseItems): JSX.Element => (
                <span
                  tabIndex={0}
                  className={`tx-plan-row-drag-handle${
                    draggingItemId === props.item_id ? ' dragging' : ''
                  }`}
                  title="Drag to reorder or move to another phase"
                  aria-label="Drag to reorder or move to another phase"
                  draggable={true}
                  onMouseDown={(ev: React.MouseEvent): void => {
                    ev.stopPropagation()
                  }}
                  onDragStart={(ev: React.DragEvent): void => {
                    ev.dataTransfer.setData(
                      TX_PLAN_ITEM_DRAG_MIME,
                      stringifyTxPlanItemDragPayload({
                        itemId: props.item_id,
                        fromPhaseOriginalIdx: originalIdx,
                      }),
                    )
                    ev.dataTransfer.effectAllowed = 'move'
                    const draggedId = props.item_id
                    window.setTimeout(() => {
                      setDraggingItemId(draggedId)
                    }, 0)
                    ev.stopPropagation()
                  }}
                  onDragEnd={(): void => {
                    window.setTimeout(() => {
                      interactionRef.current.clearTreatmentPlanDragUi()
                    }, 0)
                  }}
                  onKeyDown={(ev: React.KeyboardEvent): void => {
                    ev.preventDefault()
                  }}
                >
                  <i className="mgc mgc_dots_fill" aria-hidden />
                </span>
              ),
            } as ColumnModel,
          ]
        : []),
      {
        field: 'code',
        headerText: 'Code',
        allowSorting: true,
        width: '8%',
      },
      {
        field: 'description',
        headerText: 'Description',
        allowSorting: true,
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '25%',
      },
      {
        field: 'provider',
        headerText: 'Provider',
        width: '15%',
        template: isEditMode
          ? (props: IPhaseItems & { provider?: string }) => {
              const rowKey = txPlanProcedureProviderHighlightRowKey(
                originalIdx,
                props,
              )
              const showProviderError =
                missingProviderRowKeys?.has(rowKey) ?? false
              return (
                <div
                  className={
                    showProviderError
                      ? 'tx-plan-provider-select-wrap tx-plan-provider-select-wrap--error'
                      : 'tx-plan-provider-select-wrap'
                  }
                >
                  <Form.Select
                    size="sm"
                    value={props.provider_id ?? ''}
                    aria-invalid={showProviderError}
                    onChange={(e) => {
                      onProcedureProviderChange({
                        tabKey,
                        phaseOriginalIdx: originalIdx,
                        item: props,
                        providerId: e.target.value,
                      })
                    }}
                  >
                    <option value="">Select Provider</option>
                    {providerSelectOptionsForRow(
                      providerList,
                      providerLookupList,
                      props.provider_id,
                    ).map((option) => (
                      <option
                        key={option.provider_id}
                        value={option.provider_id}
                        title={`Dr. ${getFullName(option)}`}
                      >
                        {`Dr. ${getFullName(option)}`}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )
            }
          : (props: IPhaseItems & { provider?: string }) => {
              const provider = providerLookupList.find(
                (p) => p.provider_id === props.provider_id,
              )
              return provider ? (
                <span>{`Dr. ${getFullName(provider)}`}</span>
              ) : (
                <span>-</span>
              )
            },
      },
      {
        field: 'surface',
        headerText: 'T/S',
        width: '8%',
        template: isEditMode
          ? toothSurfaceTemplate
          : (props: IPhaseItems & { surface?: string | null }) => {
              const toothSurfaceEntries = getTSFromToothSurfaces(
                props.tooth_surfaces,
              )
              const surfaceEntries = parseSurfaceString(props.surface)
              const parsedEntries =
                toothSurfaceEntries.length > 0
                  ? toothSurfaceEntries
                  : surfaceEntries
              if (parsedEntries.length === 0) return <span>-</span>

              return (
                <div className="template-cell-tooth-surface">
                  {formatToothSurfaceEntriesForDisplay(parsedEntries)}
                </div>
              )
            },
      },
      {
        field: 'charges',
        headerText: 'Clinic fee',
        allowSorting: true,
        width: '7%',
        template: (props: IPhaseItems & { charges?: number }) => {
          const feeEditKey = getTxPlanFeeEditKey(
            tabKey,
            originalIdx,
            props.item_id,
          )
          return (
            <TxPlanFeeEditCell
              charges={props.charges ?? 0}
              isEditMode={isEditMode}
              allowManualFeeOverride={
                allowManualFeeOverride && Boolean(onProcedureFeeChange)
              }
              isActiveEditor={editingFeeKey === feeEditKey}
              onStartEdit={() => setEditingFeeKey(feeEditKey)}
              onCancelEdit={() => setEditingFeeKey(null)}
              onSave={(clinicFee) => {
                onProcedureFeeChange?.({
                  tabKey,
                  phaseOriginalIdx: originalIdx,
                  item: props,
                  clinicFee,
                })
              }}
            />
          )
        },
      },
      {
        field: 'allowed_amt',
        headerText: 'Allowed amt.',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { allowed_amount?: number | null }) => (
          <span>{formatNumber(props.allowed_amount ?? 0, true)}</span>
        ),
      },
      {
        field: 'write_off',
        headerText: 'Write-off',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { write_off?: number | null }) => (
          <span>{formatNumber(props.write_off ?? 0, true)}</span>
        ),
      },
      {
        field: 'adjustments',
        headerText: 'Discount',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { adjustments?: number }) => (
          <span>{formatNumber(props.adjustments ?? 0, true)}</span>
        ),
      },
      {
        field: 'insurance',
        headerText: 'Est. ins. pay',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { insurance?: number }) => (
          <span>{formatNumber(props.insurance ?? 0, true)}</span>
        ),
      },
      {
        field: 'outstanding',
        headerText: 'Est. pt. res.',
        clipMode: ClipMode.EllipsisWithTooltip,
        width: '7%',
        template: (props: IPhaseItems & { outstanding?: number }) => (
          <span>{formatNumber(props.outstanding ?? 0, true)}</span>
        ),
      },
      ...(isEditMode
        ? [
            {
              field: 'actions',
              headerText: '',
              width: '40px',
              textAlign: 'Center' as const,
              template: (props: IPhaseItems) => (
                <i
                  className="mgc mgc_delete_2_fill"
                  style={{ cursor: 'pointer' }}
                  title="Remove"
                  onClick={() => {
                    interactionRef.current.saveHistorySnapshot(tabKey)
                    setFormStates((prev) => {
                      const prevState = prev[tabKey]
                      if (!prevState) return prev
                      return {
                        ...prev,
                        [tabKey]: {
                          ...prevState,
                          phases: prevState.phases.map((p, i) =>
                            i === originalIdx
                              ? {
                                  ...p,
                                  items: p.items.map((item: IPhaseItems) => ({
                                    ...item,
                                    is_hidden:
                                      item.item_id === props.item_id
                                        ? true
                                        : item.is_hidden,
                                  })),
                                }
                              : p,
                          ),
                        },
                      }
                    })
                  }}
                />
              ),
            } as ColumnModel,
          ]
        : []),
    ]
    return filterTxPlanInsuranceEstimateColumns(
      builtColumns,
      showInsuranceEstimate,
    )
  }, [
    isEditMode,
    draggingItemId,
    originalIdx,
    tabKey,
    providerList,
    providerLookupList,
    missingProviderRowKeys,
    allowManualFeeOverride,
    showInsuranceEstimate,
    editingFeeKey,
    onProcedureProviderChange,
    onProcedureFeeChange,
    setFormStates,
    setDraggingItemId,
    interactionRef,
  ])

  return (
    <DataGridComponent
      columns={columns}
      data={rowModels}
      allowPaging={false}
      allowSorting={true}
      allowSelection={false}
      enableVirtualization={enableVirtualization}
    />
  )
}

const TxPlanPhaseProcedureGrid = memo(TxPlanPhaseProcedureGridInner)

export default TxPlanPhaseProcedureGrid
